import React, { useEffect } from "react";
import "../assets/stylesheets/Privacy.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useBrand } from "../branding/BrandContext";
import { BrandMark } from "../branding/BrandText";

export default function Privacy() {
  const brand = useBrand();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
    <Header />
    <div className="privacyPage">
      <section className="privacyContent">
        <div className="container narrow">

          <h1>Privacy Policy</h1>
          <p className="tagline">
            <BrandMark /> Privacy Policy &amp; Data Protection Notice
          </p>
          <p><strong>Last Updated:</strong> December 15, 2025</p>

          <h2>1. Introduction</h2>
          <p>
            <BrandMark />, operated by {brand.legalEntityName} (“<BrandMark />,” “we,” “us,” or “our”),
            operates a digital marketplace platform that may connect homeowners with
            independent home service professionals (“Service Providers”).
          </p>
          <p>
            This Privacy Policy explains how we may collect, use, process, disclose,
            transfer, store, and safeguard Personal Information when you access or use
            our website, tools, surveys, or related services (collectively, the “Service”).
          </p>
          <p>
            “Personal Information” means information that identifies, relates to,
            describes, is reasonably capable of being associated with, or could reasonably
            be linked, directly or indirectly, with a particular individual or household.
          </p>

          <h2>2. Information We May Collect</h2>

          <h3>Information You Provide</h3>
          <ul>
            <li>Name, email address, and phone number</li>
            <li>Street address, ZIP code, and project location details</li>
            <li>Project descriptions, preferences, and uploaded images</li>
            <li>Communication preferences</li>
          </ul>

          <h3>Automatically Collected Information</h3>
          <ul>
            <li>IP address</li>
            <li>Device type, browser type, and operating system</li>
            <li>Usage data and interaction behavior</li>
            <li>Referring URLs, timestamps, and session data</li>
          </ul>

          <p>
            We may also collect additional information where permitted by law,
            where voluntarily provided by you, or where necessary to operate,
            maintain, secure, or improve the Service.
          </p>

          <h2>3. Sensitive Personal Information (CPRA Notice)</h2>
          <p>
            We may collect certain categories of information that could be considered
            “Sensitive Personal Information” under applicable California law, including:
          </p>
          <ul>
            <li>Precise geolocation information (if voluntarily provided)</li>
            <li>Financial information for transaction processing (if applicable)</li>
            <li>Account login credentials (if accounts are created)</li>
          </ul>
          <p>
            Sensitive Personal Information may be used only as reasonably necessary
            to provide requested services, ensure security, prevent fraud,
            comply with legal obligations, or for other purposes permitted by law.
          </p>
          <p>
            We do not intentionally use Sensitive Personal Information
            to infer characteristics unrelated to the intended purpose of your request.
          </p>

          <h2>4. How We May Use Your Information</h2>
          <ul>
            <li>To facilitate potential matching with Service Providers</li>
            <li>To generate technology-assisted project summaries or structured reports</li>
            <li>To facilitate communication between users and Service Providers</li>
            <li>To operate, maintain, and improve the Service</li>
            <li>To detect, investigate, and prevent fraud or misuse</li>
            <li>To comply with applicable legal or regulatory obligations</li>
          </ul>
          <p>
            We may combine information collected from different sources
            to improve functionality, accuracy, and operational efficiency.
          </p>

          <h2>5. AI Processing Disclosure</h2>
          <p>
            We may use artificial intelligence, machine learning models,
            or automated systems to analyze submitted information,
            including uploaded images, to generate structured project summaries,
            insights, or estimates.
          </p>
          <p>
            AI-generated outputs are informational in nature and may not be
            complete, accurate, or definitive. Users should independently
            verify all project details before relying on such outputs.
          </p>

          <h2>6. Legal Basis for Processing (GDPR Notice)</h2>
          <p>
            If you are located in the European Economic Area (EEA),
            United Kingdom, or similar jurisdictions, we may process Personal Information
            under one or more of the following lawful bases:
          </p>
          <ul>
            <li><strong>Consent:</strong> Where you voluntarily submit information or opt into communications.</li>
            <li><strong>Contractual Necessity:</strong> Where processing is necessary to provide requested services.</li>
            <li><strong>Legitimate Interests:</strong> Where reasonably necessary to improve, secure, or operate the Service.</li>
            <li><strong>Legal Obligation:</strong> Where required to comply with applicable laws.</li>
          </ul>

          <h2>7. International Data Transfers</h2>
          <p>
            Personal Information may be processed, stored, or transferred
            in the United States or other jurisdictions where data protection
            laws may differ from those in your country of residence.
          </p>
          <p>
            Where required by applicable law, we may implement appropriate safeguards,
            such as Standard Contractual Clauses or other lawful transfer mechanisms.
          </p>

          <h2>8. Sharing of Information</h2>

          <h3>With Service Providers</h3>
          <p>
            Your project information may be shared with independent Service Providers
            operating in your geographic area for the purpose of evaluating
            and potentially responding to your request.
          </p>

          <h3>With Vendors</h3>
          <p>
            We may share Personal Information with third-party vendors,
            service providers, hosting providers, analytics services,
            cloud infrastructure partners, payment processors,
            and marketing platforms who process data on our behalf
            and subject to appropriate contractual safeguards.
          </p>

          <h3>Legal Requirements</h3>
          <p>
            We may disclose Personal Information if required to do so by law,
            subpoena, court order, governmental request, or if we believe
            such disclosure is reasonably necessary to protect our rights,
            property, users, or the public.
          </p>

          <h2>9. Data Retention</h2>
          <p>
            We may retain Personal Information for as long as reasonably necessary
            to provide the Service, comply with legal obligations,
            resolve disputes, enforce agreements, or for legitimate business purposes.
          </p>

          <h2>10. Your Privacy Rights</h2>
          <p>
            Depending on your jurisdiction, you may have rights to:
          </p>
          <ul>
            <li>Access your Personal Information</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion of certain data</li>
            <li>Object to or restrict certain processing</li>
            <li>Request portability</li>
            <li>Opt out of sale or sharing (where applicable)</li>
            <li>Limit use of Sensitive Personal Information</li>
          </ul>
          <p>
            To exercise these rights, contact: <strong>help@homehelper.biz</strong>
          </p>

          <h2>11. Cookies & Tracking Technologies</h2>
          <p>
            We may use cookies and similar tracking technologies to operate,
            analyze, and improve the Service, personalize content,
            and support marketing activities.
          </p>
          <ul>
            <li>Essential cookies</li>
            <li>Analytics cookies</li>
            <li>Advertising cookies</li>
          </ul>
          <p>
            You may be presented with a cookie consent banner where required by law.
            Cookie preferences may be managed through browser settings
            or consent management tools where available.
          </p>

          <h3>Cookie Policy</h3>
          <p>
            Essential cookies may be required for functionality.
            Analytics and advertising cookies may be disabled
            through consent mechanisms where applicable.
          </p>

          <h2>12. Data Processing Addendum (For Service Providers)</h2>
          <p>
            Service Providers who receive Personal Information through the
            <BrandMark /> platform may act as independent controllers of such data
            once received. They are independently responsible for complying
            with applicable privacy and data protection laws.
          </p>
          <p>
            <BrandMark /> may require contractual commitments
            from Service Providers regarding appropriate data safeguards.
          </p>

          <h2>13. Security</h2>
          <p>
            We implement commercially reasonable administrative,
            technical, and organizational safeguards intended to protect
            Personal Information.
          </p>
          <p>
            However, no method of transmission or storage is completely secure,
            and use of the Service is at your own risk.
          </p>

          <h2>14. Children’s Privacy</h2>
          <p>
            The Service is not intended for individuals under 16 years of age.
            We do not knowingly collect Personal Information from minors.
          </p>

          <h2>15. Changes to This Policy</h2>
          <p>
            We may modify or update this Privacy Policy from time to time.
            Continued use of the Service after changes constitutes acceptance
            of the revised policy.
          </p>

          <h2>16. Contact Us</h2>
          <p>
            <strong>{brand.legalEntityName}</strong><br />
            Email: help@homehelper.biz<br />
            Website: {brand.consumerBaseUrl}
          </p>
        </div>
      </section>
    </div>
    <Footer />
    </>
  );
}
